import React, { useState, useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import CreateMeetingModal from '../components/CreateMeetingModal';
import SummaryModal from '../components/SummaryModal';
import { supabase } from '../utils/supabase';
import type { Contact } from '../types/supabase';

interface Summary {
  id: string;
  title: string;
  date: string;
  time: string;
  double: {
    name: string;
  };
}

const Calendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [summaries, setSummaries] = useState<Summary[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<Summary | null>(null);

  useEffect(() => {
    const fetchSummaries = async () => {
      try {
        // Fetch summaries
        const { data: summariesData, error: summariesError } = await supabase
          .from('summaries')
          .select(`
            id,
            title,
            date,
            time,
            double:double_id (
              name
            )
          `)
          .gte('date', new Date().toISOString().split('T')[0]);

        if (summariesError) throw summariesError;

        // Fetch contacts
        const { data: contactsData, error: contactsError } = await supabase
          .from('contacts')
          .select('*');

        if (contactsError) throw contactsError;

        setSummaries(summariesData || []);
        setContacts(contactsData || []);
      } catch (err) {
        console.error('Error fetching summaries:', err);
        setError('Failed to load calendar events');
      } finally {
        setLoading(false);
      }
    };

    fetchSummaries();
  }, []);
  
  // Get calendar data
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Add days from previous month
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDay - i),
        isCurrentMonth: false
      });
    }
    
    // Add days from current month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    
    // Add days from next month
    const remainingDays = 42 - days.length; // Always show 6 weeks
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }
    
    return days;
  };

  const formatDate = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const getDayEvents = (dateStr: string) => {
    return summaries.filter(summary => summary.date === dateStr);
  };

  const days = getDaysInMonth(currentDate);
  const today = new Date();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthYear = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const isToday = (date: Date) => {
    return formatDate(date) === formatDate(today);
  };

  const isPastEvent = (date: string) => {
    return new Date(date) < new Date(new Date().setHours(0, 0, 0, 0));
  };

  const handleEventClick = (event: typeof calls[0], e: React.MouseEvent) => {
    e.stopPropagation();
    if (isPastEvent(event.date)) {
      const summary = summaries.find(s => s.id === event.id);
      if (summary) {
        setSelectedEvent(summary);
        setShowSummaryModal(true);
      }
    }
  };

  const handleDayClick = (date: Date) => {
    setSelectedDate(formatDate(date));
    setShowCreateModal(true);
  };

  const handleCreateMeeting = useCallback((meeting: {
    title: string;
    contactId: number;
    type: 'inbound' | 'outbound';
    date: string;
    time: string;
  }) => {
    // In a real app, this would make an API call to create the meeting
    console.log('Creating meeting:', meeting);
    setShowCreateModal(false);
    setSelectedDate('');
  }, []);

  return (
    <div className="animate-fade-in">
      <div className="mb-8 flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Calendar</h1>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={prevMonth}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <h2 className="text-xl font-semibold min-w-40 text-center">{monthYear}</h2>
            <button
              onClick={nextMonth}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-xl border-2 border-dark shadow-retro">
        <div className="flex justify-end mb-6">
          <button 
            onClick={() => setShowCreateModal(true)}
            className="button-primary flex items-center gap-2"
          >
            Create a Meeting
          </button>
        </div>

        <div className="calendar-grid mb-2">
          {weekDays.map(day => (
            <div key={day} className="text-center font-semibold text-gray-600 py-2">
              {day}
            </div>
          ))}
        </div>
        
        <div className="calendar-grid">
          {days.map((day, index) => {
            const dateStr = formatDate(day.date);
            const dayEvents = getDayEvents(dateStr);
            
            return (
              <div
                key={index}
                onClick={() => handleDayClick(day.date)}
                className={`calendar-day ${
                  isToday(day.date) ? 'current' : ''
                } ${
                  day.isCurrentMonth ? 'bg-white shadow-retro' : 'other-month bg-gray-50'
                } cursor-pointer`}
              >
                <div className="font-medium mb-1">{day.date.getDate()}</div>
                <div className="space-y-1">
                  {dayEvents.map(event => (
                    <div
                      key={event.id}
                      className="calendar-event"
                      onClick={(e) => handleEventClick(event, e)}
                      title={`${event.title} - ${event.time} with ${event.double.name}`}
                    >
                      {event.title}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {showCreateModal && (
        <CreateMeetingModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateMeeting}
          contacts={contacts}
          initialDate={selectedDate}
        />
      )}
      
      {showSummaryModal && selectedEvent && (
        <SummaryModal
          summary={selectedEvent}
          onClose={() => {
            setShowSummaryModal(false);
            setSelectedEvent(null);
          }}
        />
      )}
    </div>
  );
};

export default Calendar;